/**
 * Pure risk taxonomy helpers for the interaction matrix.
 *
 * Safety rule: an unknown or invalid risk value is NEVER treated as a low risk
 * ("safe") value. Everything that cannot be mapped to a known category falls
 * back to the explicit "unknown" level.
 */
import type { InteractionRisk } from "@/types/restlicht-db";

export type Level =
  | "synergy"
  | "reduced"
  | "caution"
  | "highrisk"
  | "contra"
  | "safe"
  | "unknown";

export type Marker = "serotonin" | "cardio" | "serotonin_cardio" | "respiratory";

export type MatrixInteraction = { a: string; b: string; level: Level; markers?: Marker[] };

/** Exhaustive mapping of the database enum `interaction_risk` to display levels. */
export const RISK_TO_LEVEL: Record<InteractionRisk, Level> = {
  low_risk_synergy: "synergy",
  low_risk_no_synergy: "safe",
  low_risk_decrease: "reduced",
  caution: "caution",
  unsafe: "highrisk",
  dangerous: "contra",
  unknown: "unknown",
};

export const LEVEL_ORDER: Level[] = [
  "safe",
  "synergy",
  "reduced",
  "caution",
  "highrisk",
  "contra",
  "unknown",
];

export function isInteractionRisk(value: unknown): value is InteractionRisk {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(RISK_TO_LEVEL, value);
}

/**
 * Maps a raw database value to a display level.
 * `invalid` is true when the value is not part of the known enum. Callers can
 * log or surface that without downgrading the shown risk.
 */
export function riskToLevel(raw: unknown): { level: Level; invalid: boolean } {
  if (isInteractionRisk(raw)) return { level: RISK_TO_LEVEL[raw], invalid: false };
  return { level: "unknown", invalid: true };
}

const KNOWN_MARKERS: Marker[] = ["serotonin", "cardio", "serotonin_cardio", "respiratory"];

function toMarkers(raw: unknown): Marker[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const list = raw.filter((m): m is Marker => KNOWN_MARKERS.includes(m as Marker));
  return list.length ? list : undefined;
}

export type RawInteractionRow = {
  substance_low_id: string;
  substance_high_id: string;
  risk_level: unknown;
  markers: unknown;
};

export type BuildResult = {
  interactions: MatrixInteraction[];
  invalidRiskValues: string[];
  unresolvedRows: number;
};

/**
 * Builds the matrix interaction list. Rows with an unmappable risk value are
 * kept and shown as "unknown" instead of being silently dropped.
 */
export function buildInteractions(
  rows: RawInteractionRow[],
  idToName: Map<string, string>,
): BuildResult {
  const interactions: MatrixInteraction[] = [];
  const invalidRiskValues: string[] = [];
  let unresolvedRows = 0;

  for (const row of rows) {
    const a = idToName.get(row.substance_low_id);
    const b = idToName.get(row.substance_high_id);
    if (!a || !b) {
      unresolvedRows += 1;
      continue;
    }
    const { level, invalid } = riskToLevel(row.risk_level);
    if (invalid) invalidRiskValues.push(String(row.risk_level));
    interactions.push({ a, b, level, markers: toMarkers(row.markers) });
  }

  return { interactions, invalidRiskValues, unresolvedRows };
}

/** Order-independent pair lookup. Returns null for the diagonal and empty input. */
export function findInteraction(
  interactions: MatrixInteraction[],
  a: string,
  b: string,
): MatrixInteraction | null {
  if (!a || !b || a === b) return null;
  return (
    interactions.find((i) => (i.a === a && i.b === b) || (i.a === b && i.b === a)) ?? null
  );
}
